import { isTruthyDbBool } from 'helpers/check-type-filter-options';
import { isFixedPricingMode } from 'helpers/pricing-mode';

export const parseExchangeRate = (raw, fallback = null) => {
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) {
    return fallback;
  }
  return n;
};

export const parseOptionalNumber = (raw, fallback = null) => {
  if (raw === '' || raw === null || raw === undefined) {
    return fallback;
  }
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
  if (Number.isNaN(n)) {
    return fallback;
  }
  return n;
};

export const parseNonNegativeNumber = (raw, fallback = null) => {
  const n = parseOptionalNumber(raw, fallback);
  if (n == null || n < 0) {
    return fallback;
  }
  return n;
};

export const formatNumericInputValue = value => {
  if (value == null || value === '') {
    return '';
  }
  const n = Number(value);
  if (Number.isNaN(n)) {
    return String(value);
  }
  return n.toFixed(2);
};

export const normalizeNumericInputOnBlur = (
  raw,
  { fallback = null, min = 0, decimals = 2 } = {},
) => {
  if (raw === '' || raw == null) {
    if (fallback != null) {
      return Number(fallback).toFixed(decimals);
    }
    return '';
  }
  const n = parseFloat(String(raw).trim());
  if (Number.isNaN(n) || n < min) {
    if (fallback != null) {
      return Number(fallback).toFixed(decimals);
    }
    return '';
  }
  return n.toFixed(decimals);
};

export const numericDraftKey = (rowId, field) => `${rowId}:${field}`;

export const parseNumericDraftValue = raw => {
  if (raw === '' || raw == null) {
    return null;
  }
  const n = parseFloat(String(raw).trim());
  if (Number.isNaN(n) || n < 0) {
    return undefined;
  }
  return n;
};

export const mergeNumericDraftsIntoPendingEdits = (
  pendingEdits,
  numericDrafts,
  rows,
  exchangeRate,
) => {
  if (exchangeRate == null || exchangeRate <= 0) {
    return pendingEdits;
  }
  const next = { ...pendingEdits };
  Object.entries(numericDrafts).forEach(([key, raw]) => {
    const sep = key.indexOf(':');
    if (sep < 0) {
      return;
    }
    const rowId = key.slice(0, sep);
    const field = key.slice(sep + 1);
    const row = rows.find(r => String(r.id) === rowId);
    if (!row) {
      return;
    }
    const value = parseNumericDraftValue(raw);
    if (value === undefined) {
      return;
    }
    const rowEdits = { ...(next[rowId] ?? {}) };
    const pending = { ...next, [rowId]: rowEdits };
    if (field === 'provider_cost' || field === 'floor') {
      next[rowId] = { ...rowEdits, [field]: value };
      return;
    }
    if (!field.startsWith('price_')) {
      return;
    }
    const baseCurrency = getBaseCurrency(row, pending);
    const anchorField = getAnchorPriceField(baseCurrency);
    if (field !== anchorField) {
      return;
    }
    next[rowId] = {
      ...rowEdits,
      ...derivePricesFromAnchor(baseCurrency, value, exchangeRate),
    };
  });
  return next;
};

export const pendingEditsWithNumericDrafts = (pendingEdits, numericDrafts, row, exchangeRate) => {
  if (exchangeRate == null || exchangeRate <= 0) {
    return pendingEdits;
  }
  const anchorField = getAnchorPriceField(getBaseCurrency(row, pendingEdits));
  const anchorKey = numericDraftKey(row.id, anchorField);
  const providerKey = numericDraftKey(row.id, 'provider_cost');
  const floorKey = numericDraftKey(row.id, 'floor');
  const hasAnchorDraft = anchorKey in numericDrafts;
  const hasProviderDraft = providerKey in numericDrafts;
  const hasFloorDraft = floorKey in numericDrafts;
  if (!hasAnchorDraft && !hasProviderDraft && !hasFloorDraft) {
    return pendingEdits;
  }

  const rowEdits = { ...(pendingEdits[row.id] ?? {}) };
  if (hasAnchorDraft) {
    const value = parseNumericDraftValue(numericDrafts[anchorKey]);
    if (value !== undefined) {
      const baseCurrency = getBaseCurrency(row, { ...pendingEdits, [row.id]: rowEdits });
      Object.assign(rowEdits, derivePricesFromAnchor(baseCurrency, value, exchangeRate));
    }
  }
  if (hasProviderDraft) {
    const value = parseNumericDraftValue(numericDrafts[providerKey]);
    if (value !== undefined) {
      rowEdits.provider_cost = value;
    }
  }
  if (hasFloorDraft) {
    const value = parseNumericDraftValue(numericDrafts[floorKey]);
    if (value !== undefined) {
      rowEdits.floor = value;
    }
  }
  return { ...pendingEdits, [row.id]: rowEdits };
};

export const resolveInternalCostAud = (row, internalCostManual, internalCostNonManual) => {
  if (row?.internal_cost != null && row.internal_cost !== '') {
    return Number(row.internal_cost);
  }
  if (internalCostManual == null || internalCostNonManual == null) {
    return null;
  }
  return isTruthyDbBool(row?.is_manual) ? internalCostManual : internalCostNonManual;
};

export const round50 = value => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return null;
  }
  return Math.round(Number(value) * 2) / 2;
};

export const round2 = value => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return null;
  }
  return Math.round(Number(value) * 100) / 100;
};

export const convertProviderCostToUsd = (providerCost, baseCurrency, exchangeRate) => {
  if (exchangeRate == null || exchangeRate <= 0) {
    return null;
  }
  if (providerCost == null || providerCost === '' || Number.isNaN(Number(providerCost))) {
    return null;
  }
  const amount = Number(providerCost);
  const currency = (baseCurrency ?? 'AUD').toUpperCase();
  if (currency === 'USD') {
    return amount;
  }
  if (currency === 'AUD' || currency === 'NZD') {
    return amount * exchangeRate;
  }
  return amount;
};

export const convertInternalCostAudToUsd = (internalCostAud, exchangeRate) => {
  if (exchangeRate == null || exchangeRate <= 0) {
    return 0;
  }
  if (internalCostAud == null || internalCostAud === '' || Number.isNaN(Number(internalCostAud))) {
    return 0;
  }
  return round2(Number(internalCostAud) * exchangeRate) ?? 0;
};

export const getDisplayInternalCost = (baseCurrency, internalCostAud, exchangeRate) => {
  const currency = (baseCurrency ?? 'AUD').toUpperCase();
  if (currency === 'USD') {
    return convertInternalCostAudToUsd(internalCostAud, exchangeRate);
  }
  return internalCostAud;
};

export const getEffectivePricingField = (row, pendingEdits, field) => {
  const edited = pendingEdits?.[row.id];
  if (edited && field in edited) {
    return edited[field];
  }
  return row[field];
};

export const getBaseCurrency = (row, pendingEdits = {}) =>
  (getEffectivePricingField(row, pendingEdits, 'base_currency') ?? 'AUD').toUpperCase();

export const derivePricesFromAnchor = (anchorCurrency, anchorValue, exchangeRate) => {
  if (exchangeRate == null || exchangeRate <= 0) {
    return { price_aud: null, price_nzd: null, price_usd: null };
  }
  if (anchorValue == null || anchorValue === '' || Number.isNaN(Number(anchorValue))) {
    return { price_aud: null, price_nzd: null, price_usd: null };
  }
  const anchor = Number(anchorValue);
  const currency = (anchorCurrency ?? 'AUD').toUpperCase();

  if (currency === 'USD') {
    const audNzd = round50(anchor / exchangeRate);
    return { price_aud: audNzd, price_nzd: audNzd, price_usd: anchor };
  }

  const audNzd = anchor;
  const usd = round50(anchor * exchangeRate);
  return { price_aud: audNzd, price_nzd: audNzd, price_usd: usd };
};

export const getAnchorPriceField = baseCurrency => {
  const currency = (baseCurrency ?? 'AUD').toUpperCase();
  if (currency === 'USD') {
    return 'price_usd';
  }
  if (currency === 'NZD') {
    return 'price_nzd';
  }
  return 'price_aud';
};

export const getAnchorPriceValue = (row, pendingEdits = {}) => {
  const baseCurrency = getBaseCurrency(row, pendingEdits);
  const anchorField = getAnchorPriceField(baseCurrency);
  const anchorRaw = getEffectivePricingField(row, pendingEdits, anchorField);
  if (anchorRaw == null || anchorRaw === '' || Number.isNaN(Number(anchorRaw))) {
    return null;
  }
  return Number(anchorRaw);
};

export const getDerivedPricesForRow = (row, pendingEdits = {}, exchangeRate) => {
  const baseCurrency = getBaseCurrency(row, pendingEdits);
  return derivePricesFromAnchor(baseCurrency, getAnchorPriceValue(row, pendingEdits), exchangeRate);
};

export const getDisplayPriceForRow = (row, field, pendingEdits = {}, exchangeRate) => {
  const derived = getDerivedPricesForRow(row, pendingEdits, exchangeRate);
  const value = derived[field];
  if (value != null) {
    return value;
  }
  const raw = getEffectivePricingField(row, pendingEdits, field);
  if (raw == null || raw === '' || Number.isNaN(Number(raw))) {
    return null;
  }
  return Number(raw);
};

export const resolveFloorPrice = (row, pendingEdits = {}) => {
  if (isFixedPricingMode(row?.pricing_mode)) {
    return getAnchorPriceValue(row, pendingEdits);
  }
  const raw = getEffectivePricingField(row, pendingEdits, 'floor');
  if (raw == null || raw === '' || Number.isNaN(Number(raw))) {
    return null;
  }
  return Number(raw);
};

export const maxDiscountPctFromFloor = (sellPrice, floorPrice) => {
  const sell =
    sellPrice != null && sellPrice !== '' && !Number.isNaN(Number(sellPrice))
      ? Number(sellPrice)
      : null;
  const floor =
    floorPrice != null && floorPrice !== '' && !Number.isNaN(Number(floorPrice))
      ? Number(floorPrice)
      : null;
  if (sell == null || sell <= 0 || floor == null) {
    return null;
  }
  return ((sell - floor) / sell) * 100;
};

export const marginPctForSellPrice = (sellPrice, totalCost) => {
  const sell =
    sellPrice != null && sellPrice !== '' && !Number.isNaN(Number(sellPrice))
      ? Number(sellPrice)
      : null;
  if (sell == null || sell <= 0 || totalCost == null) {
    return null;
  }
  return ((sell - totalCost) / sell) * 100;
};

export const calculatePricingMarginInBaseCurrency = (
  row,
  pendingEdits = {},
  { exchangeRate, internalCostManual, internalCostNonManual } = {},
) => {
  const baseCurrency = getBaseCurrency(row, pendingEdits);
  const sellPrice = getAnchorPriceValue(row, pendingEdits);
  const providerCost = getEffectivePricingField(row, pendingEdits, 'provider_cost');

  const providerCostNum =
    providerCost != null && providerCost !== '' && !Number.isNaN(Number(providerCost))
      ? Number(providerCost)
      : 0;
  const rowInternalCostAud = resolveInternalCostAud(row, internalCostManual, internalCostNonManual);
  const internalCost = getDisplayInternalCost(baseCurrency, rowInternalCostAud, exchangeRate) ?? 0;
  const totalCost = round2(providerCostNum + internalCost);

  const margin = sellPrice != null && totalCost != null ? round2(sellPrice - totalCost) : null;
  const marginPct = marginPctForSellPrice(sellPrice, totalCost);
  const floorPrice = resolveFloorPrice(row, pendingEdits);
  const floorMargin =
    floorPrice != null && totalCost != null ? round2(floorPrice - totalCost) : null;
  const floorMarginPct = marginPctForSellPrice(floorPrice, totalCost);

  return {
    totalCost,
    margin,
    marginPct,
    floorPrice,
    floorMargin,
    floorMarginPct,
    baseCurrency,
  };
};

export const calculatePricingMargin = (
  row,
  pendingEdits = {},
  { exchangeRate, internalCostManual, internalCostNonManual } = {},
) => {
  const priceUsd = getEffectivePricingField(row, pendingEdits, 'price_usd');
  const providerCost = getEffectivePricingField(row, pendingEdits, 'provider_cost');
  const baseCurrency = getBaseCurrency(row, pendingEdits);

  const providerCostUsd = convertProviderCostToUsd(providerCost, baseCurrency, exchangeRate);
  const rowInternalCostAud = resolveInternalCostAud(row, internalCostManual, internalCostNonManual);
  const internalCostUsd = convertInternalCostAudToUsd(rowInternalCostAud, exchangeRate);
  const totalCostUsd = (providerCostUsd ?? 0) + internalCostUsd;

  const sellUsd =
    priceUsd != null && priceUsd !== '' && !Number.isNaN(Number(priceUsd))
      ? Number(priceUsd)
      : null;

  const marginUsd = sellUsd != null ? sellUsd - totalCostUsd : null;
  const marginPct =
    sellUsd != null && sellUsd > 0 && marginUsd != null ? (marginUsd / sellUsd) * 100 : null;

  return {
    providerCostUsd,
    internalCostUsd,
    totalCostUsd,
    marginUsd,
    marginPct,
  };
};

export const formatPricingAmount = v =>
  v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : '-';

export const pricingFieldValuesEqual = (original, edited) => {
  if (original === edited) {
    return true;
  }
  const originalEmpty = original == null || original === '';
  const editedEmpty = edited == null || edited === '';
  if (originalEmpty && editedEmpty) {
    return true;
  }
  const originalNum = Number(original);
  const editedNum = Number(edited);
  if (!Number.isNaN(originalNum) && !Number.isNaN(editedNum)) {
    return Math.abs(originalNum - editedNum) < 0.001;
  }
  return String(original).trim() === String(edited).trim();
};

export const rowHasPendingPricingEdits = (row, rowEdits) => {
  if (!rowEdits || Object.keys(rowEdits).length === 0) {
    return false;
  }
  return Object.entries(rowEdits).some(([field, value]) => {
    return !pricingFieldValuesEqual(row[field], value);
  });
};

export const pruneMatchingPendingEdits = (pendingEdits, rows) => {
  const next = {};
  rows.forEach(row => {
    const rowEdits = pendingEdits[row.id];
    if (!rowEdits) {
      return;
    }
    const pruned = {};
    Object.entries(rowEdits).forEach(([field, value]) => {
      if (!pricingFieldValuesEqual(row[field], value)) {
        pruned[field] = value;
      }
    });
    if (Object.keys(pruned).length > 0) {
      next[row.id] = pruned;
    }
  });
  return next;
};

export const formatMarginPctDisplay = marginPct => {
  if (marginPct == null || Number.isNaN(marginPct)) {
    return '-';
  }
  return `${marginPct.toFixed(1)}%`;
};

export const formatMarginDisplay = (marginAmount, marginPct) => {
  if (marginAmount == null || Number.isNaN(marginAmount)) {
    return '-';
  }
  const amount = formatPricingAmount(marginAmount);
  if (marginPct == null || Number.isNaN(marginPct)) {
    return amount;
  }
  return `${amount} (${marginPct.toFixed(1)}%)`;
};
